/**
 * TaskKanbanColumn - Coluna (fase) do Kanban de Tarefas
 * 
 * Exibe header da fase com contadores e lista de TaskCards.
 * Implementa infinite scroll via IntersectionObserver.
 * Segue padrão do KanbanColumn (Conversas).
 * 
 * @version 1.5.0
 * RELEASE: 2026-10-16 v1.5.0 - Tarefas, Retenção e Jurídico reutilizam o
 *          cabeçalho compacto de O.S., preservando cores, contadores e ações.
 * RELEASE: 2026-04-06 v1.0.0 - Criação inicial
 * RELEASE: 2026-04-08 v1.1.0 - Suporte a canDrag condicional por permissão
 * RELEASE: 2026-08-06 v1.2.0 - Coluna arrastável (reordenação de fases) +
 *          ícones de editar fase (lápis) e criar tarefa na fase (+), todos
 *          condicionados às permissões recebidas por props
 * RELEASE: 2026-08-06 v1.3.0 - Correção do arraste de colunas: droppable das
 *          tarefas movido para o container interno (não mais no mesmo nó do
 *          sortable), fallback de transição e sortable sempre registrado —
 *          paridade total com DealKanbanColumn
 * RELEASE: 2026-08-07 v1.4.0 - Cabeçalho da coluna reorganizado em 2 linhas
 *          (linha 1: cor + nome da fase + contador; linha 2: badges de atrasadas/
 *          final à esquerda e ações lápis/+/automação à direita), eliminando o
 *          truncamento agressivo do nome da fase — mesmo padrão do Kanban de Negócios

 */

import { useEffect, useRef, useCallback } from 'react';
import { useDroppable } from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Loader2, Zap, GripVertical, Plus } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { KanbanColumnHeader } from '@/components/kanban/KanbanColumnHeader';
import { TaskCard } from './TaskCard';
import { TaskPhaseEditButton, type TaskPhaseFormValues } from './AddTaskPhaseColumn';
import { useTaskKanbanColumn } from '@/hooks/useTaskKanbanColumn';
import { useRetentionPeriodsMeta } from '@/hooks/useRetentionPeriodsMeta';
import { useTaskModuleType } from '@/hooks/useTaskModuleType';
import type { TaskKanbanFilters, TaskKanbanItem } from '@/hooks/useTaskKanban';
import type { TaskPhaseCount } from '@/hooks/useTaskKanbanCounts';
import type { PhaseAction } from '@/hooks/useTaskPhasePermissions';


interface TaskKanbanColumnProps {
  phaseId: string;
  phaseName: string;
  phaseColor: string;
  phaseIsFinal?: boolean;
  count?: TaskPhaseCount;
  filters?: TaskKanbanFilters;
  onTaskClick?: (task: TaskKanbanItem) => void;
  /** Função para verificar permissão granular por fase */
  canDoOnPhase?: (phaseId: string | null | undefined, action: PhaseAction, isResponsible?: boolean) => boolean;
  /** ID do usuário logado para calcular isResponsible */
  currentUserId?: string | null;
  /** Indica se a fase possui automações vinculadas (mostra ícone de raio) */
  hasAutomation?: boolean;
  /** Quantidade de automações vinculadas à fase (exibido no tooltip) */
  automationCount?: number;
  /** Handler chamado ao clicar no ícone de raio (abre modal de automações) */
  onAutomationClick?: () => void;
  /** v1.2.0 - Permite arrastar a coluna inteira (permissão de editar fases) */
  canReorderPhases?: boolean;
  /** v1.2.0 - Exibe o lápis de edição rápida da fase */
  canEditPhase?: boolean;
  /** v1.2.0 - Salva a edição rápida da fase (upsert_task_phase) */
  onEditPhase?: (values: TaskPhaseFormValues) => Promise<void>;
  /** v1.2.0 - Exibe o botão "+" de criação de tarefa já nesta fase */
  canCreateTask?: boolean;
  /** v1.2.0 - Handler do botão "+" (abre modal de criação com a fase pré-selecionada) */
  onCreateTask?: () => void;
}

export function TaskKanbanColumn({
  phaseId,
  phaseName,
  phaseColor,
  phaseIsFinal = false,
  count,
  filters,
  onTaskClick,
  canDoOnPhase,
  currentUserId,
  hasAutomation = false,
  automationCount = 0,
  onAutomationClick,
  canReorderPhases = false,
  canEditPhase = false,
  onEditPhase,
  canCreateTask = false,
  onCreateTask,
}: TaskKanbanColumnProps) {
  // Ref para trigger de infinite scroll
  const loadMoreRef = useRef<HTMLDivElement>(null);

  // Hook de paginação infinita - carrega tarefas internamente
  const {
    tasks,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useTaskKanbanColumn(phaseId, filters);

  // v1.x — Retenção/Jurídico: metadados de período (data de solicitação ou de
  // entrada / fechamento) das tarefas visíveis nesta coluna, buscados em UMA
  // chamada (evita N+1).
  const taskModuleType = useTaskModuleType();
  const { metaById: retentionMetaById } = useRetentionPeriodsMeta(
    tasks.map((t) => t.id),
    taskModuleType === 'retention' || taskModuleType === 'legal',
  );

  // v1.3.0 - Área de drop das TAREFAS: fica em um nó INTERNO (lista de cards),
  // exatamente como no DealKanbanColumn. Manter no mesmo nó do sortable fazia
  // os dois alvos de colisão se sobreporem e impedia a reordenação de colunas.
  const { setNodeRef: setDroppableRef, isOver } = useDroppable({
    id: phaseId,
    data: { type: 'phase', phaseId },
  });

  // v1.2.0 - Coluna arrastável (reordenação de fases), mesma arquitetura do CRM
  const {
    attributes,
    listeners,
    setNodeRef: setSortableRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: `column-${phaseId}`,
    data: { type: 'column', phaseId },
  });

  // Estilo aplicado durante o arraste da coluna
  // (fallback de transição garante que as colunas vizinhas deslizem)
  const columnStyle = {
    transform: CSS.Transform.toString(transform),
    transition: transition || 'transform 200ms ease',
  };



  // IntersectionObserver para infinite scroll
  useEffect(() => {
    if (!hasNextPage || isFetchingNextPage || isLoading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          fetchNextPage();
        }
      },
      { threshold: 0.1, rootMargin: '100px' }
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, isLoading, fetchNextPage]);

  /** Calcula se o card pode ser arrastado (can_change_phase com contexto de responsável) */
  const canDragTask = useCallback((task: TaskKanbanItem): boolean => {
    if (!canDoOnPhase) return true; // Sem função = permitir (fallback)
    const isResponsible = currentUserId ? task.assigned_to === currentUserId : undefined;
    return canDoOnPhase(task.phase_id, 'can_change_phase', isResponsible);
  }, [canDoOnPhase, currentUserId]);

  // IDs para SortableContext
  const taskIds = tasks.map(t => t.id);

  // Contagem exibida (prefere stats do servidor)
  const displayCount = count?.total_count ?? tasks.length;
  const overdueCount = count?.overdue_count ?? 0;

  return (
    <div
      // v1.3.0 - Apenas o sortable no nó externo (droppable de tarefas é interno)
      ref={setSortableRef}
      style={columnStyle}
      className={cn(
        'flex flex-col w-72 h-full shrink-0 rounded-lg bg-muted/30',
        'border border-border/50 overflow-hidden group transition-all duration-200',
        isOver && 'ring-2 ring-primary/50 bg-primary/5',
        isDragging && 'opacity-0'
      )}
    >

      <KanbanColumnHeader compact name={phaseName} color={phaseColor} count={displayCount} overdue={overdueCount} isFinal={phaseIsFinal}
        handle={canReorderPhases ? <Button variant="ghost" size="icon" className="h-6 w-5 cursor-grab text-muted-foreground hover:text-foreground hover:bg-muted/50" title="Arraste para reordenar" aria-label="Arraste para reordenar fase" {...attributes} {...listeners}><GripVertical className="h-4 w-4" /></Button> : undefined}
        actions={<>
            {/* v1.2.0 - Edição rápida da fase (lápis) */}
            {canEditPhase && onEditPhase && (
              <TaskPhaseEditButton
                phaseName={phaseName}
                phaseColor={phaseColor || '#6b7280'}
                phaseIsFinal={phaseIsFinal}
                onSave={onEditPhase}
              />
            )}

            {/* v1.2.0 - Criar tarefa já nesta fase (+) */}
            {canCreateTask && onCreateTask && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 text-muted-foreground hover:text-foreground"
                      onClick={(e) => {
                        e.stopPropagation();
                        onCreateTask();
                      }}
                    >
                      <Plus className="h-3.5 w-3.5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="top">Nova tarefa nesta fase</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}

            {/* Ícone de automação (raio) — exibido quando a fase tem fluxos vinculados */}
            {hasAutomation && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 text-amber-500 hover:text-amber-600 hover:bg-amber-500/10"
                      onClick={(e) => {
                        e.stopPropagation();
                        onAutomationClick?.();
                      }}
                    >
                      <Zap className="h-3.5 w-3.5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    {automationCount} automação{automationCount !== 1 ? 'ões' : ''} ativa{automationCount !== 1 ? 's' : ''}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
        </>}
      />


      {/* Área de conteúdo com scroll — v1.3.0: o droppable de tarefas fica
          neste container interno (mesma estrutura do DealKanbanColumn) */}
      <div className="flex-1 min-h-0">
        <ScrollArea className="h-full">
          <div ref={setDroppableRef} className="p-2 space-y-2 min-h-[100px]">

            <SortableContext items={taskIds} strategy={verticalListSortingStrategy}>
              {/* Loading inicial */}
              {isLoading && (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              )}

              {/* Cards de tarefas — com canDrag baseado em permissão */}
              {tasks.map((task) => (
                <TaskCard
                  key={task.id}
                  task={task}
                  /* v1.x — Retenção: metadados de período buscados em lote */
                  retentionMeta={retentionMetaById[task.id] ?? null}
                  /* v2.2.0 — destaque/campos específicos de Retenção e Jurídico */
                  moduleType={taskModuleType}

                  onClick={() => onTaskClick?.(task)}
                  canDrag={canDragTask(task)}
                />
              ))}

              {/* Estado vazio */}
              {!isLoading && tasks.length === 0 && (
                <div className="text-center py-8 text-xs text-muted-foreground">
                  Nenhuma tarefa nesta fase
                </div>
              )}

              {/* Trigger para infinite scroll */}
              {hasNextPage && (
                <div ref={loadMoreRef} className="flex items-center justify-center py-2">
                  {isFetchingNextPage && (
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  )}
                </div>
              )}
            </SortableContext>
          </div>
        </ScrollArea>
      </div>
    </div>
  );
}
